import type { CampaignListItem, CampaignListItemServer } from '@metapic-apps/shared'
import { type ComputedRef, type Ref, ref, toValue, watch } from 'vue'

import type { AdvertiserType } from '@/lib'
import { normalizeCampaignListItem } from '@/lib/adapters/campaigns.adapters'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import { useErrorStore } from '@/stores/error.stores'

export const useGetCampaigns = (
  advertiser: ComputedRef<AdvertiserType>,
  page: Ref<number | undefined>,
  searchQuery?: Ref<string>,
) => {
  const data = ref<CampaignListItem[] | undefined>()
  const totalPages = ref<number>(0)
  const links = ref<{ first?: string; last?: string; prev?: string; next?: string }>({})
  const isLoading = ref(true)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchCampaigns = async () => {
    try {
      data.value = undefined
      isLoading.value = true
      error.value = null

      const pageVal = toValue(page)
      const searchQueryVal = toValue(searchQuery)
      const advertiserId = advertiser.value?.id

      if (advertiserId) {
        const response = await request.get(
          `/v2/offers?custom_sort_by=active_first&page=${pageVal}&advertiser_id=${advertiserId}&show_for_advertiser=1${searchQueryVal ? `&query=${encodeURIComponent(searchQueryVal)}` : ''}`,
        )
        data.value = response.data.data.map((item: CampaignListItemServer) =>
          normalizeCampaignListItem(item),
        )
        totalPages.value = response.data.meta.last_page
        links.value = response.data.links
      }
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      isLoading.value = false
    } finally {
      isLoading.value = false
    }
  }

  watch(
    [() => page?.value, () => searchQuery?.value, () => advertiser.value],
    async () => {
      await fetchCampaigns()
    },
    { immediate: true },
  )

  return {
    data,
    links,
    totalPages,
    fetchCampaigns,
    isLoading,
    error,
  }
}
